// Durable write queue for Game Lite.
//
// The bug this fixes: `submitAnswer` used to await the RPC, catch any error,
// log it to the console and carry on — so one dropped packet silently threw the
// answer away. The player saw their pick accepted and the results screen later
// told them "You didn't answer this one".
//
// Now every write is described by a SERIALIZABLE descriptor ({ rpc, args }),
// mirrored to localStorage, and retried until it lands — across backgrounded
// tabs, reconnects, and full page reloads. Nothing is dropped on the floor; the
// only way an entry leaves the queue is by succeeding, or by the caller
// explicitly cancelling it. The exits added since are both told to the app: a
// write the server refuses for good is dropped and reported (onDrop), and a
// write refused because the server already holds it is settled (onSettle),
// which loses nothing.
//
// Safe because every solo write RPC is idempotent (migration 125): solo_answers
// upserts on (participant_id, wine_id, question_id), and solo_finish_wine /
// solo_complete_wine recompute the totals from solo_answers rather than adding
// to them. Replaying one can't double-count.
//
// This is the Game Lite sibling of apps/game/src/utils/requestQueue.js. It is a
// separate implementation on purpose: the party game's queue is load-bearing for
// live sessions, and Lite's needs are narrower (RPC ops only) but stricter (it
// must expose what's in flight so the UI can reflect unsent work).

const STORAGE_KEY = 'tasted:solo:writeQueue:v1';
const MAX_ATTEMPTS_BEFORE_PARK = 4;
const BASE_DELAY_MS = 800;
const MAX_DELAY_MS = 20000;
// An entry older than this is abandoned: the tasting is long over, and replaying
// it into a later session would be worse than losing it.
const MAX_AGE_MS = 12 * 60 * 60 * 1000;
// How long a write refused for its token waits for this device to get a working
// one back — the readmit flow re-mints it — before it is given up on. Mirrors
// apps/game's TOKEN_WAIT_MS, which exists for the same reason: an answer that
// is only refused because the phone lost its seat is not a bad answer.
const TOKEN_WAIT_MS = 30 * 60 * 1000;

const noop = () => {};

// What a lost write was, for telling the player. Read from the RPC name, which
// every descriptor carries and soloWrites sets from one place; the id prefix is
// built from the same builders but meta can be null, and the id is only a key.
// A finish (the reveal's score recompute) is `other`: the player cannot act on
// it, and the answers and rating it recomputes from are counted on their own.
const KIND_BY_RPC = {
  solo_submit_answer: 'answer',
  solo_complete_wine: 'rating',
};

export function dropKind(item) {
  return KIND_BY_RPC[item?.rpc] || 'other';
}

const DROP_KINDS = ['answer', 'rating', 'other'];
const noDrops = () => ({ answer: 0, rating: 0, other: 0 });

/**
 * The ids an entry was queued `after`, as a list. The field is a single id on
 * every entry written before a write could wait on several (and still is when
 * there is only one), a list of ids otherwise, and absent on entries older
 * than the field. Anything else reads as no dependency.
 */
function afterIds(item) {
  const after = item?.after;
  if (typeof after === 'string') return after ? [after] : [];
  if (!Array.isArray(after)) return [];
  return after.filter((id) => typeof id === 'string' && id);
}

/** What enqueue stores for `after`: null, the one id, or the list of ids. */
function normaliseAfter(after, ownId) {
  const ids = [...new Set(afterIds({ after }))].filter((id) => id !== ownId);
  if (ids.length === 0) return null;
  return ids.length === 1 ? ids[0] : ids;
}

export class WriteQueue {
  constructor({ storageKey = STORAGE_KEY, executor = null } = {}) {
    this.storageKey = storageKey;
    this.queue = [];
    this.listeners = new Set();
    this.isProcessing = false;
    this._drain = null;
    // Writes that left the queue WITHOUT the server storing them: refused for
    // good, expired by age, or timed out waiting for a token. Only ever goes up.
    // A success, cancel(), a supersede or a settled write (the server already
    // holds it) is not counted. RAM only, deliberately: a dropped write is gone
    // from storage, so a reload has nothing to count twice, and whatever that
    // reload expires is counted then, as a new drop.
    this.droppedCount = 0;
    // The same drops, tallied by what was lost ('answer', 'rating', 'other').
    // The three always sum to droppedCount. Same rules: only ever goes up, RAM
    // only, and a cancel() or a supersede is not counted.
    this.droppedByKind = noDrops();
    // How much of that tally the player has been told about, by kind (see
    // acknowledgeDrops). The rest is OWED: a drop is owed from the moment it
    // is counted, whoever was or was not subscribed then, until the UI says it
    // has shown it. Kept here, beside the tally it is subtracted from, so the
    // two cannot drift apart. RAM only as well: a reload owes what it drops.
    this.acknowledgedByKind = noDrops();
    this.executor = executor; // set via setExecutor(); injected so tests need no network
    this.started = false;
    this.onSuccess = noop;   // (item, data) => void — the server's verdict
    this.onDrop = noop;      // (item, error) => void — unsendable, gave up
    this.onSettle = noop;    // (item, error) => void — the server already holds it
    // Reads the token this device currently holds, so an entry parked on a
    // refused token knows when a readmit has given it a new one.
    this.tokenReader = null;
  }

  /** Install the reader for the device's current access token. */
  setTokenReader(fn) {
    this.tokenReader = typeof fn === 'function' ? fn : null;
  }

  /**
   * Install the function that performs one descriptor. It must resolve with the
   * RPC's data on success and THROW on failure. Throwing an error tagged
   * `permanent` means "this can never succeed" (e.g. the question is not in
   * this tasting) — the queue drops it. The one exception is an error tagged
   * `tokenRefused`: that means the write was fine and only the device's token
   * was not, so the entry is parked to wait for a readmit rather than binned.
   * The other is an error tagged `settled`: the server refused the write
   * because it already holds what the write is about and kept it. The entry
   * leaves the queue, and that is not a loss (see setOnSettle).
   */
  setExecutor(fn) {
    this.executor = fn;
  }

  /** Called with (item, data) when a queued write finally lands. */
  setOnSuccess(fn) {
    this.onSuccess = typeof fn === 'function' ? fn : noop;
  }

  /** Called with (item, error) when a write is abandoned as unsendable. */
  setOnDrop(fn) {
    this.onDrop = typeof fn === 'function' ? fn : noop;
  }

  /**
   * Called with (item, error) when a write leaves the queue because the server
   * already holds what it is about. Nothing was lost, so nothing is counted
   * and onDrop is not called. There is no server verdict to pass on either:
   * the refusal says the stored one stands, not what it was.
   */
  setOnSettle(fn) {
    this.onSettle = typeof fn === 'function' ? fn : noop;
  }

  /**
   * Queue a write.
   * @param {object} p
   * @param {string} p.id      stable, deterministic id — re-enqueuing the same
   *                           id REPLACES the pending payload (a player changing
   *                           their answer supersedes the unsent one) rather
   *                           than queuing a second write.
   * @param {string} p.rpc     RPC name
   * @param {object} p.args    RPC arguments (must be JSON-serializable)
   * @param {object} [p.meta]  arbitrary serializable tag the UI can read back
   *                           (we use it to know which question is unsent)
   * @param {number} [p.priority] higher runs first (answers before leaderboard)
   * @param {string|string[]} [p.after] id, or ids, of the writes this one
   *                           must not be sent ahead of. While an entry with
   *                           one of those ids is in the queue, in ANY state,
   *                           this one waits; it goes in the normal order once
   *                           they have all left. See _isBlocked.
   */
  enqueue({ id, rpc, args, meta = null, priority = 0, after = null }) {
    if (!id || !rpc) return;
    const entry = {
      id,
      rpc,
      args,
      meta,
      priority,
      after: normaliseAfter(after, id),
      attempts: 0,
      parked: false,
      addedAt: Date.now(),
    };
    const existing = this.queue.findIndex((it) => it.id === id);
    if (existing !== -1) {
      // Supersede: keep the original addedAt so an entry can't be kept alive
      // forever by repeated edits, but take the new payload and un-park it.
      entry.addedAt = this.queue[existing].addedAt;
      this.queue[existing] = entry;
    } else {
      this.queue.push(entry);
    }
    this.queue.sort((a, b) => b.priority - a.priority);
    this._save();
    this._notify();
    this.process();
  }

  /** Drop a queued write (the caller no longer wants it sent). */
  cancel(id) {
    const i = this.queue.findIndex((it) => it.id === id);
    if (i === -1) return;
    this.queue.splice(i, 1);
    this._save();
    this._notify();
  }

  /** Everything still waiting to reach the server. */
  getPending() {
    return this.queue.map((it) => ({
      id: it.id,
      rpc: it.rpc,
      meta: it.meta,
      attempts: it.attempts,
      parked: it.parked,
    }));
  }

  /** True while any write for this predicate is unsent. */
  hasPending(predicate) {
    if (!predicate) return this.queue.length > 0;
    return this.queue.some((it) => predicate(it));
  }

  get size() {
    return this.queue.length;
  }

  /**
   * The drops the player has not been told about yet, by kind: the tally less
   * what has been acknowledged. It does not depend on anyone having watched
   * the queue while the writes were dropped, so a subscriber that arrives late
   * (a bar that mounts after a restored write has expired) still finds them.
   */
  getOwedDrops() {
    const owed = noDrops();
    for (const kind of DROP_KINDS) {
      owed[kind] = Math.max(
        0,
        (this.droppedByKind[kind] || 0) - (this.acknowledgedByKind[kind] || 0)
      );
    }
    return owed;
  }

  /**
   * The UI has told the player about these drops ({ answer, rating, other });
   * they are not owed again. Never acknowledges more than is owed, so a stale
   * or repeated call cannot hide a later drop.
   */
  acknowledgeDrops(told) {
    const owed = this.getOwedDrops();
    let changed = false;
    for (const kind of DROP_KINDS) {
      const count = Math.min(owed[kind], Math.max(0, Math.floor(Number(told?.[kind]) || 0)));
      if (count === 0) continue;
      this.acknowledgedByKind[kind] = (this.acknowledgedByKind[kind] || 0) + count;
      changed = true;
    }
    if (changed) this._notify();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Drain the queue. Re-entrant-safe: a call made while a drain is already
   * running returns THAT drain's promise rather than a resolved one, so
   * `await process()` always means "the queue has been worked", never "someone
   * else is working it". The loop re-reads `this.queue` each pass, so entries
   * added mid-drain are picked up by the running drain.
   */
  process() {
    if (this.isProcessing) return this._drain || Promise.resolve();
    if (!this.executor) return Promise.resolve();
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return Promise.resolve();
    }
    this._drain = this._process();
    return this._drain;
  }

  async _process() {
    this.isProcessing = true;
    try {
      // Bound the drain so a permanently-failing entry can't spin: each pass
      // takes the first unparked entry, and parks it if it keeps failing. An
      // entry held behind others (`after`) is passed over until they have all
      // left the queue, then taken in its normal turn by a later pass.
      for (;;) {
        this._expire();
        this._expireTokenWaits();
        // A readmit may have landed since the last pass: anything held for a
        // token it no longer needs to wait for is released here.
        for (const it of this.queue) {
          if (it.awaitingToken) this._isAwaitingToken(it);
        }
        const item = this.queue.find((it) => !it.parked && !this._isBlocked(it));
        if (!item) break;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) break;

        let ok = false;
        let failure = null;
        let result;
        try {
          result = await this.executor(item);
          ok = true;
        } catch (err) {
          failure = err;
        }

        // The queue may have been mutated during the await (superseded or
        // cancelled). Locate by id, never by index.
        const idx = this.queue.findIndex((it) => it.id === item.id);
        if (idx === -1) continue; // cancelled mid-flight — respect that
        const current = this.queue[idx];
        if (current !== item) continue; // superseded — the new payload will run

        if (ok) {
          this.queue.splice(idx, 1);
          this._save();
          this._notify();
          try {
            this.onSuccess(item, result);
          } catch {
            /* a UI callback must never stall the drain */
          }
          continue;
        }

        if (failure?.permanent) {
          // A refusal aimed at the TOKEN, not the write. The answer is good;
          // this device just isn't holding a key the server accepts. Hold it
          // for the readmit rather than throwing away a real answer — the
          // mistake apps/game already learned not to make (its A9 audit).
          if (failure.tokenRefused && this._parkForToken(current, failure)) {
            this._save();
            this._notify();
            continue;
          }
          // Refused because the server already holds what this write is about,
          // and kept it. It is not retried (the answer would be the same) and
          // it is not a drop: nothing the player gave was lost. It leaves the
          // queue like a success does, which also releases anything queued
          // `after` it.
          if (failure.settled) {
            this.queue.splice(idx, 1);
            this._save();
            this._notify();
            try {
              this.onSettle(item, failure);
            } catch {
              /* a UI callback must never stall the drain */
            }
            continue;
          }
          // Anything else can never succeed. Drop it and tell the app, so it
          // can surface something honest instead of a silent hang.
          this.queue.splice(idx, 1);
          this._countDrop(item);
          this._save();
          this._notify();
          try {
            this.onDrop(item, failure);
          } catch {
            /* ignore */
          }
          continue;
        }

        current.attempts += 1;
        current.lastError = failure?.message || String(failure);
        if (current.attempts >= MAX_ATTEMPTS_BEFORE_PARK) {
          // Park rather than discard — a parked write is still durable and is
          // re-armed by the next reconnect, visibility change, or reload. This
          // is the whole point: an answer is never thrown away.
          current.parked = true;
          this._save();
          this._notify();
          continue;
        }
        this._save();
        this._notify();
        const delay = Math.min(BASE_DELAY_MS * 2 ** (current.attempts - 1), MAX_DELAY_MS);
        await new Promise((r) => setTimeout(r, delay));
      }
    } finally {
      this.isProcessing = false;
      this._drain = null;
    }
  }

  /** Un-park everything and drain — call on reconnect / tab focus / reload. */
  retryAll() {
    let changed = false;
    for (const item of this.queue) {
      // An entry waiting on a token is not un-parked by a reconnect: the
      // network was never the problem, and retrying would just burn the
      // attempt budget against the same refusal. _isAwaitingToken releases it
      // the moment this device actually holds a different token.
      if (item.awaitingToken && this._isAwaitingToken(item)) continue;
      if (item.parked) {
        item.parked = false;
        item.attempts = 0;
        changed = true;
      }
    }
    if (changed) {
      this._save();
      this._notify();
    }
    this.process();
  }

  /** Wire browser events once. Idempotent. */
  start() {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    window.addEventListener('online', () => this.retryAll());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.retryAll();
    });
    // A backgrounded tab gets far less CPU; nudge the queue when we're about to
    // lose it so a pending answer gets one more chance before the tab sleeps.
    window.addEventListener('pagehide', () => this.process());
  }

  /** Re-read anything a previous page load left behind, then drain it. */
  rehydrate() {
    const stored = this._read();
    if (stored.length === 0) return;
    const byId = new Set(this.queue.map((it) => it.id));
    for (const entry of stored) {
      if (!entry?.id || !entry?.rpc || byId.has(entry.id)) continue;
      this.queue.push({
        ...entry,
        attempts: 0,
        // A fresh page load earns a fresh attempt — unless the entry is still
        // waiting on a token, which a reload does not supply.
        parked: Boolean(entry.awaitingToken),
      });
    }
    this.queue.sort((a, b) => b.priority - a.priority);
    // Entries past the replay window are dropped here, before anything is sent
    // and whether or not the phone is online. They are counted and owed like
    // any other drop (getOwedDrops), so it does not matter that no subscriber
    // ever sees them waiting.
    this._expire();
    this._notify();
    this.process();
  }

  /** Resolve once the queue is empty, or after `timeoutMs`. */
  async flush(timeoutMs = 5000) {
    this.retryAll();
    const deadline = Date.now() + timeoutMs;
    while (this.queue.length > 0 && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 100));
    }
    return this.queue.length === 0;
  }

  /** Empty the queue on purpose (logout). Like cancel(), not counted as drops. */
  clear() {
    this.queue = [];
    this._save();
    this._notify();
  }

  // ── internals ────────────────────────────────────────────────────────────

  /**
   * True while any write this entry was queued `after` is still in the queue.
   *
   * "In the queue" is deliberately every state: waiting, in flight, backing
   * off, parked after repeated failures, or held for a token. The reason an
   * entry asks to go second is that the server reads the first one's result,
   * so the only safe moment is after that write has LEFT. It leaves by
   * succeeding, by being settled (the server already holds it), or by being
   * dropped (refused for good, timed out, cancelled). A dropped or settled
   * blocker therefore releases its dependent; nothing waits forever on a
   * write that is never going to land.
   *
   * An id that is not in the queue is no dependency: it already landed, or was
   * never queued. An entry saved by a build that predates the field has none,
   * and one saved when the field held a single id is read as a list of one.
   * A blocker whose own chain leads back to this entry is ignored, so two
   * writes that each name the other are both sent instead of deadlocking. Only
   * that blocker is ignored: the entry still waits for the others it names.
   */
  _isBlocked(item) {
    for (const id of afterIds(item)) {
      if (id === item.id) continue; // self-reference
      const blocker = this.queue.find((it) => it.id === id);
      if (!blocker) continue;
      if (this._leadsTo(blocker, item.id)) continue; // a cycle through us
      return true;
    }
    return false;
  }

  /** True when following `after` from `from` reaches the entry `targetId`. */
  _leadsTo(from, targetId) {
    const seen = new Set([from.id]);
    const stack = [from];
    while (stack.length > 0) {
      for (const id of afterIds(stack.pop())) {
        if (id === targetId) return true;
        if (seen.has(id)) continue; // a loop further down the chain, not ours
        seen.add(id);
        const entry = this.queue.find((it) => it.id === id);
        if (entry) stack.push(entry);
      }
    }
    return false;
  }

  /** Record one write that left the queue without the server storing it. */
  _countDrop(item) {
    this.droppedCount += 1;
    this.droppedByKind[dropKind(item)] += 1;
  }

  _expire() {
    const cutoff = Date.now() - MAX_AGE_MS;
    const stale = this.queue.filter((it) => it.addedAt < cutoff);
    if (stale.length === 0) return;
    this.queue = this.queue.filter((it) => it.addedAt >= cutoff);
    for (const item of stale) this._countDrop(item);
    this._save();
    this._notify();
    // These used to vanish without a word — the one place in this queue where
    // a player's answer was thrown away and nothing, not the UI and not us,
    // was told. Report it like any other drop.
    for (const item of stale) {
      const err = new Error('Write abandoned: older than the 12h replay window');
      err.expired = true;
      try {
        this.onDrop(item, err);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Hold an entry until this device has a working token again, instead of
   * discarding it. `tokenAt` is what the entry was refused with; the executor
   * compares it against the current token and releases the entry as soon as
   * they differ (a readmit has re-minted it). Gives up after TOKEN_WAIT_MS.
   */
  _parkForToken(item, error) {
    const since = Number.isFinite(item.tokenWaitSince) ? item.tokenWaitSince : Date.now();
    if (Date.now() - since >= TOKEN_WAIT_MS) return false;
    item.tokenWaitSince = since;
    item.awaitingToken = error?.refusedToken ?? null;
    item.attempts = 0;
    item.parked = true;
    return true;
  }

  /** Still waiting on a token: released once the stored one differs. */
  _isAwaitingToken(item) {
    if (!item.awaitingToken) return false;
    const current = this.tokenReader ? this.tokenReader() : null;
    if (current && current !== item.awaitingToken) {
      item.awaitingToken = null; // re-minted — send it with the new one
      item.parked = false;
      return false;
    }
    return true;
  }

  /** Drop entries that waited out TOKEN_WAIT_MS for a token that never came. */
  _expireTokenWaits(now = Date.now()) {
    const expired = this.queue.filter(
      (it) => it.awaitingToken && now - (it.tokenWaitSince || now) >= TOKEN_WAIT_MS
    );
    for (const item of expired) {
      const i = this.queue.indexOf(item);
      if (i !== -1) this.queue.splice(i, 1);
      const err = new Error('Write abandoned: this device never got a working token back');
      err.tokenExpired = true;
      try {
        this.onDrop(item, err);
      } catch {
        /* ignore */
      }
    }
    if (expired.length > 0) {
      for (const item of expired) this._countDrop(item);
      this._save();
      this._notify();
    }
    return expired.length;
  }

  _notify() {
    const pending = this.getPending();
    for (const cb of this.listeners) {
      try {
        cb(pending);
      } catch {
        /* one bad subscriber must not stall the queue */
      }
    }
  }

  _save() {
    if (typeof window === 'undefined') return;
    try {
      if (this.queue.length === 0) {
        window.localStorage.removeItem(this.storageKey);
        return;
      }
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify(
          this.queue.map(({ id, rpc, args, meta, priority, addedAt, after, awaitingToken, tokenWaitSince }) => ({
            id, rpc, args, meta, priority, addedAt,
            // Survives the reload too: a rating still unsent when the tab died
            // must still go ahead of the reveal that waits on it. Stored as
            // enqueue left it: one id as a string, several as a list.
            after: after || undefined,
            // Survives the reload, so a wait doesn't restart its 30 minutes
            // every time the tab is reopened.
            awaitingToken: awaitingToken || undefined,
            tokenWaitSince: Number.isFinite(tokenWaitSince) ? tokenWaitSince : undefined,
          }))
        )
      );
    } catch {
      /* private mode / quota — the in-RAM queue still works for this session */
    }
  }

  _read() {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}

export const writeQueue = new WriteQueue();
export { STORAGE_KEY, MAX_ATTEMPTS_BEFORE_PARK, MAX_AGE_MS, TOKEN_WAIT_MS, noop };
